import React, {useState, useEffect, createContext,FC } from 'react';
import axios from "axios";
import {useAuth0} from "@auth0/auth0-react";

interface IDeviceType {
    deviceType:string
}

export interface IDPool {
    identities?: { [key: string]: Identity };
    devices?:    { [key: string]: Devices } ;
    deviceType: string;
    refreshInterval?:number;
    statusRefreshInterval?:number;
    error?:string;
    accessToken?:string;
    loading?:boolean;
    poolDeviceCount?:IPoolCounts;
    deviceTypes?:IDeviceType[];
    updateDeviceType?:(newDevice: string)=>void;
    updateDeviceData?:()=>void;
    applianceStatus?:IApplianceStatus[];
}

export interface Identity {
    secretStatus: number;
    serialNumber: string;
    deviceType:   string;
    assignedMac:  string;
}

export interface Devices {
    mac:          string;
    ip:           string;
    serialNumber: string;
    version:      string;
    deviceStatus: number;
    checkStatus:  number;
    deviceType:   string;
    lastUpdate:   Date;
}

export interface IApplianceStatus {
    logMessage:        string;
    deviceStatus:      number;
    elapsedTime:       number;
    lastUpdate:        string;
    serial:            string;
    macAddress:        string;
    ipAddress:         string;
}


interface IPoolCounts {
    pool?: { [key: number]: IPoolDeviceCounts}
}
interface IPoolDeviceCounts {
    deviceCount?: { [key: string]: number }
}

const defaultDeviceTypes:IDeviceType[] = [];

const defaultApplianceStatusPool:IApplianceStatus[] = [];
const defaultPoolCounts:IPoolCounts = {};
const defaultState = {
    identities:{},
    devices: {},
    deviceType: "P340",
    refreshInterval:0,
    statusRefreshInterval:0,
    loading:true,
    error:"",
    accessToken:"",
    poolDeviceCount:defaultPoolCounts,
    deviceTypes:defaultDeviceTypes,
    applianceStatus:defaultApplianceStatusPool
}

const defaultRefreshInterval = 3000000 // every 5 minutes update entire device list & devices types
const defaultStatusRefreshInterval = 5000 // every 1 second(s) check pool counts & status update

export const IDPoolContext = React.createContext<IDPool>(defaultState);

export const identityServer =  process.env.NODE_ENV==="development" ?  "http://127.0.0.1:8000" : ""
export const identityServerWS =  process.env.NODE_ENV==="development" ?  "127.0.0.1:8000" : document.location.host
export const apiVersion = "v1"

export const IDPoolProvider: FC = ({children}) => {
    const [deviceType,setDeviceType] = useState(defaultState.deviceType);
    const [devices,setDevices] = useState(defaultState.devices);
    const [refreshInterval, setRefreshInterval] = React.useState(defaultState.refreshInterval);
    const [statusRefreshInterval, setStatusRefreshInterval] = React.useState(defaultState.statusRefreshInterval);
    const [loading, setLoading]: [boolean, (loading: boolean) => void] = React.useState<boolean>(defaultState.loading);
    const [deviceTypes, setDeviceTypes]: [IDeviceType[], (deviceTypes: IDeviceType[]) => void] = React.useState(defaultState.deviceTypes);
    const [applianceStatus, setApplianceStatus]: [IApplianceStatus[], (applianceStatus: IApplianceStatus[]) => void] = React.useState(defaultState.applianceStatus);
    const [poolDeviceCount,setPoolDeviceCount]: [IPoolCounts,(poolDeviceCount: IPoolCounts) => void ] = React.useState(defaultState.poolDeviceCount);
    const [error, setError]: [string, (error: string) => void] = React.useState(defaultState.error);
    const [accessToken, setAccessToken]: [string, (error:string) => void] = React.useState(defaultState.error);

    const { getAccessTokenSilently } = useAuth0();

    React.useEffect(() => {
        let interval:any
        let statusInterval:any

        async function getAccessToken() {

            try {
                const accessToken = await getAccessTokenSilently();
                setAccessToken(accessToken)
            } catch (e) {
                console.log( "An error occured getting the access token")
            }
        }
        getAccessToken()

        if (statusRefreshInterval && statusRefreshInterval > 0) {
            statusInterval = setInterval(fetchStatusData, statusRefreshInterval);
        } else {
            fetchStatusData();
            setStatusRefreshInterval(defaultStatusRefreshInterval);

        }

        if (refreshInterval && refreshInterval > 0) {
            interval = setInterval(updateDeviceData, refreshInterval);
        } else {
            updateDeviceData();
            setRefreshInterval(defaultRefreshInterval)
        }

        return () => {
            clearInterval(interval);
            clearInterval(statusInterval);
        }

    }, [getAccessTokenSilently,statusRefreshInterval,refreshInterval,deviceType,error,loading]);


    const updateDeviceData = () => {

        axios
            .get<IDeviceType[]>(identityServer + "/configs/config-v1.json?cb=" + Date.now(), {
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${accessToken}`,
                },
                timeout: refreshInterval / 2
            }).then(response => {

            let dt = [];
            for (let dtype in response.data) {
                let devicedata = {deviceType: dtype}
                dt.push(devicedata);
            }
            setDeviceTypes(dt)
            setError("")
            setLoading(false)
        }).catch(ex => {
            let error = "An Unexpected error has occured";
            setError(error)
            setLoading(true);

        });

        axios.get<IDPool>(identityServer+"/api/"+apiVersion+"/logs/devices?cb="+Date.now(), {
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${accessToken}`,
            },
            timeout: refreshInterval / 2
        }).then(response => {
            //let dtype = response.data.deviceType
          //  setDeviceType(dtype)
            if (response.data != undefined)
                setDevices(response.data)
            setError("")
            setLoading(false)
        }).catch(ex => {
            let error = "An Unexpected error has occured";
            setError(error);
            setLoading(true);
        });
    }


    const updateDeviceType = (newDevice: string) => {
        setDeviceType(newDevice)

        axios.post(identityServer+"/api/"+apiVersion+"/devicetype",{

            deviceType:newDevice
        }, {
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${accessToken}`,
            }
        },).then(response => {
            console.log(response)
        }).catch(ex => {
            console.log(ex)
        })
    }

    const fetchStatusData = () => {

        axios.get<IPoolCounts>(identityServer+"/api/"+apiVersion+"/identity/counts?cb="+Date.now(), {
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${accessToken}`,
            },
            timeout: refreshInterval / 2
        }).then(response => {
            setPoolDeviceCount(response.data)

            setError("")
            setLoading(false)
        }).catch(ex => {
            let error = "An Unexpected error has occured";
            setError(error);
            setLoading(true);
        });

        axios.get<string>(identityServer+"/api/"+apiVersion+"/devicetype?cb="+Date.now(), {
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${accessToken}`,
            },
            timeout: statusRefreshInterval / 2
        }).then(response => {
            if (response.data != undefined)
                setDeviceType(response.data)
            setError("")
            setLoading(false)
        }).catch(ex => {
            let error = "An Unexpected error has occured";
            setError(error);
            setLoading(true);
        });

        axios
            .get<IApplianceStatus[]>(identityServer+"/api/"+apiVersion+"/appliance/status?cb="+Date.now(), {
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${accessToken}`,
                },
                timeout: statusRefreshInterval / 2
            }).then(response => {
            setApplianceStatus(response.data)
            setError("")
            setLoading(false)
        }).catch(ex => {
            let error = "An Unexpected error has occured";
            setError(error)
            setLoading(true);
        });
    }



    return (
        <IDPoolContext.Provider value={{
            deviceType,
            devices,
            deviceTypes,
            updateDeviceType,
            updateDeviceData,
            applianceStatus,
            error,
            loading,
            poolDeviceCount,
            accessToken
        }}>
            {children}
        </IDPoolContext.Provider>
    )

}


